import * as THREE from "three";
import { vineCurve, vineLength, type AsteroidBody, type VineCurve, type VineDef, type WorldDef } from "@dyson/shared";

/** In front of the rock, just behind characters; strands and leaves sit a hair either side of it so they cross over and under. */
const Z = -0.1;
/** Segments per block along a strand. */
const PER_BLOCK = 3;

/** Strand tones, dark to light: outline, shadow, mid, light. One palette per strand, so the braid reads as separate vines. */
const STRANDS = [
  ["#14240f", "#2b4d1f", "#3f6e2b", "#5f9440"],
  ["#1d1a0e", "#45401f", "#665c2c", "#8a7d3f"],
  ["#10231a", "#24503a", "#357052", "#52996d"],
].map((p) => p.map((h) => new THREE.Color(h)));
/** Leaves: outline, shadow half, lit half. */
const LEAF = ["#183a14", "#3b7a2c", "#6bb246"].map((h) => new THREE.Color(h));
const WITHERED = ["#2e2a26", "#55504a", "#77706a", "#938b82"].map((h) => new THREE.Color(h));
const DEAD_LEAF = ["#3a2c1c", "#6b5232", "#8c6f45"].map((h) => new THREE.Color(h));

/** A small, fast, seeded random (mulberry32). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface VineMesh {
  mesh: THREE.Mesh;
  /** What its geometry was built for: rebuilt when a vine is cut or its rock dies. */
  key: string;
  uniforms: { uA: { value: THREE.Vector2 }; uB: { value: THREE.Vector2 }; uC: { value: THREE.Vector2 }; uN: { value: THREE.Vector2 } };
}

/**
 * The vines of the vined rocks in view (see VineDef): two or three strands
 * braided round each other, with little leaves along them, in flat pixel-art
 * tones with a dark outline. A cut vine is two ends hanging loose from their
 * lobes; one on a dead rock is withered and bare.
 *
 * Each vine's geometry is built once, in the vine's own terms (how far along
 * it, how far to the side), and the vertex shader lays it along the curve, so
 * swaying it each frame is just moving the curve's pull point.
 */
export class VineViews {
  readonly group = new THREE.Group();
  private readonly meshes = new Map<string, VineMesh>();

  update(world: WorldDef | undefined, cut: ReadonlySet<string>, asteroids: Map<string, AsteroidBody>, time: number, near: { x: number; y: number }, reach: number): void {
    const seen = new Set<string>();
    for (const v of world?.vines ?? []) {
      const curve = vineCurve(world!, v);
      if (!curve || Math.hypot(curve.a.x - near.x, curve.a.y - near.y) > reach + Math.hypot(curve.b.x - curve.a.x, curve.b.y - curve.a.y)) continue;
      seen.add(v.id);
      const dead = !!asteroids.get(v.rock)?.def.dead;
      const isCut = cut.has(v.id) || dead;
      const key = `${isCut}:${dead}`;
      let m = this.meshes.get(v.id);
      if (m && m.key !== key) {
        m.mesh.geometry.dispose();
        m.mesh.geometry = build(v, vineLength(curve), isCut, dead);
        m.key = key;
      }
      if (!m) {
        m = make(build(v, vineLength(curve), isCut, dead));
        m.key = key;
        this.group.add(m.mesh);
        this.meshes.set(v.id, m);
      }
      // Sway: the pull point drifts side to side, slowly, each vine on its own beat.
      const len = Math.hypot(curve.b.x - curve.a.x, curve.b.y - curve.a.y) || 1;
      const nx = -(curve.b.y - curve.a.y) / len;
      const ny = (curve.b.x - curve.a.x) / len;
      const sway = Math.sin(time * 0.7 + (v.seed % 628) / 100) * (v.sag * 0.25 + 1);
      const u = m.uniforms;
      u.uA.value.set(curve.a.x, curve.a.y);
      u.uB.value.set(curve.b.x, curve.b.y);
      u.uC.value.set(curve.c.x + nx * sway, curve.c.y + ny * sway);
      u.uN.value.set(nx, ny);
    }
    for (const [id, m] of this.meshes) {
      if (seen.has(id)) continue;
      this.group.remove(m.mesh);
      m.mesh.geometry.dispose();
      (m.mesh.material as THREE.Material).dispose();
      this.meshes.delete(id);
    }
  }

  dispose(): void {
    for (const m of this.meshes.values()) {
      m.mesh.geometry.dispose();
      (m.mesh.material as THREE.Material).dispose();
    }
    this.meshes.clear();
  }
}

/** A vine's mesh: a basic vertex-coloured material whose vertex shader lays the geometry along the curve in its uniforms. */
function make(geometry: THREE.BufferGeometry): VineMesh {
  const uniforms = { uA: { value: new THREE.Vector2() }, uB: { value: new THREE.Vector2() }, uC: { value: new THREE.Vector2() }, uN: { value: new THREE.Vector2() } };
  const material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nuniform vec2 uA, uB, uC, uN;\nattribute vec2 vine;")
      .replace(
        "#include <begin_vertex>",
        `float t = position.x;
        float s = 1.0 - t;
        vec2 p = s * s * uA + 2.0 * s * t * uC + t * t * uB;
        vec2 tg = 2.0 * s * (uC - uA) + 2.0 * t * (uB - uC);
        tg /= max(length(tg), 1e-5);
        vec2 nr = vec2(-tg.y, tg.x);
        // position.y: across the vine; position.z: along it (leaves); vine.x: droop of a cut end; vine.y: depth.
        vec3 transformed = vec3(p + nr * position.y + tg * position.z + uN * vine.x, ${Z.toFixed(2)} + vine.y);`,
      );
  };
  material.customProgramCacheKey = () => "vine";
  const mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
  mesh.geometry = geometry;
  mesh.frustumCulled = false;
  return { mesh, key: "", uniforms };
}

/**
 * A vine's geometry in its own terms: for each vertex, how far along it (t,
 * 0..1), across it and along it from there (blocks), how far a cut end droops,
 * and its depth. Its strands twist round each other, a quarter of the
 * thickness out from the middle, the one in front a tone lighter; its leaves
 * stick out from the braid at an angle, alternately each side.
 */
function build(v: VineDef, len: number, cut: boolean, dead: boolean): THREE.BufferGeometry {
  const r = rng(v.seed);
  const pos: number[] = [];
  const extra: number[] = [];
  const col: number[] = [];
  const strands = v.thickness > 1.3 || r() < 0.4 ? 3 : 2;
  /** How far each strand swings out from the middle, and how thick it is (half widths, blocks). */
  const amp = v.thickness * 0.26;
  const half = v.thickness * (strands === 3 ? 0.19 : 0.23);
  /** Blocks per full twist. */
  const period = Math.max(2, v.thickness * 2.4);
  const segs = Math.max(8, Math.ceil(len * PER_BLOCK));
  const twist = (len / period) * Math.PI * 2;

  const quad = (t0: number, t1: number, a0: number, a1: number, b0: number, b1: number, d0: number, d1: number, z: number, c: THREE.Color) => {
    // Corners (t, across): (t0, a0) (t0, b0) (t1, a1) (t1, b1).
    pos.push(t0, a0, 0, t0, b0, 0, t1, a1, 0, t0, b0, 0, t1, b1, 0, t1, a1, 0);
    extra.push(d0, z, d0, z, d1, z, d0, z, d1, z, d1, z);
    for (let k = 0; k < 6; k++) col.push(c.r, c.g, c.b);
  };
  const tri = (t: number, p: [number, number][], d: number, z: number, c: THREE.Color) => {
    for (const [across, along] of p) {
      pos.push(t, across, along);
      extra.push(d, z);
      col.push(c.r, c.g, c.b);
    }
  };

  // The pieces drawn: the whole vine, or its two ends hanging loose a third of the way each.
  const pieces: { from: number; to: number; anchor: number }[] = cut
    ? [{ from: 0, to: 1 / 3, anchor: 0 }, { from: 2 / 3, to: 1, anchor: 1 }]
    : [{ from: 0, to: 1, anchor: -1 }];
  for (const piece of pieces) {
    const n = Math.max(4, Math.round(segs * (piece.to - piece.from)));
    const at = (i: number) => piece.from + ((piece.to - piece.from) * i) / n;
    /** How far along its loose end (0 at the rock, 1 at the tip), for cut ends. */
    const loose = (t: number) => (piece.anchor < 0 ? 0 : Math.abs(t - piece.anchor) * 3);
    const droop = (t: number) => loose(t) ** 2 * v.sag * 0.6;
    const taper = (t: number) => 1 - loose(t) * 0.6;

    for (let k = 0; k < strands; k++) {
      const pal = dead ? WITHERED : STRANDS[(k + v.seed) % STRANDS.length]!;
      const phase = (k / strands) * Math.PI * 2;
      const centre = (t: number) => Math.sin(t * twist + phase) * amp * taper(t);
      /** In front (1) or behind (-1) the others at t. */
      const front = (t: number) => Math.cos(t * twist + phase);
      for (let i = 0; i < n; i++) {
        const t0 = at(i);
        const t1 = at(i + 1);
        const c0 = centre(t0);
        const c1 = centre(t1);
        const w0 = half * taper(t0);
        const w1 = half * taper(t1);
        const d0 = droop(t0);
        const d1 = droop(t1);
        const f = front((t0 + t1) / 2);
        const z = f * 0.02;
        // The outline: a little wider, just behind.
        const o = 0.07;
        quad(t0, t1, c0 - w0 - o, c1 - w1 - o, c0 + w0 + o, c1 + w1 + o, d0, d1, z - 0.004, pal[0]!);
        // Flat bands across it, lit along one side: shadow, mid, light. A strand behind the others is a tone darker.
        const dark = f < -0.3 ? 1 : 0;
        const bands: [number, number, THREE.Color][] = [
          [-1, -0.25, pal[Math.max(1, 1 - dark)]!],
          [-0.25, 0.45, pal[2 - dark]!],
          [0.45, 1, pal[3 - dark]!],
        ];
        for (const [a, b, c] of bands) quad(t0, t1, c0 + w0 * a, c1 + w1 * a, c0 + w0 * b, c1 + w1 * b, d0, d1, z, c);
      }
    }

    // Leaves: about one a block, alternately each side, pointing a little toward the vine's far end (or every which way on a loose end).
    const leaves = dead ? Math.round(len * (piece.to - piece.from) * 0.15) : Math.round(len * (piece.to - piece.from) * 1.1);
    const pal = dead ? DEAD_LEAF : LEAF;
    for (let k = 0; k < leaves; k++) {
      const t = piece.from + (piece.to - piece.from) * ((k + r() * 0.8) / Math.max(1, leaves));
      const side = (k & 1 ? 1 : -1) * (r() < 0.15 ? -1 : 1);
      const size = (0.45 + r() * 0.45) * (dead ? 0.7 : 1) * Math.min(1.3, v.thickness);
      const lean = (0.35 + r() * 0.6) * (r() < 0.3 ? -1 : 1);
      // The leaf's axis (across, along) from where it leaves the braid.
      const ax = Math.cos(lean) * side;
      const al = Math.sin(lean);
      const base: [number, number] = [side * amp * taper(t) * 0.9, 0];
      // A rounded leaf: widest a third of the way out, to a point. `at(f, w)`: f along the leaf, w across it (+ left).
      const at = (f: number, w: number, grow = 0): [number, number] => {
        const l = size * f + grow * (f > 0.99 ? 1 : 0);
        const c = size * w + grow * Math.sign(w);
        return [base[0] + ax * l - al * c, base[1] + al * l + ax * c];
      };
      const half = (s: 1 | -1, grow: number) => [at(0, 0), at(0.15, 0.22 * s, grow), at(0.4, 0.3 * s, grow), at(0.7, 0.2 * s, grow), at(1, 0, grow)];
      const fan = (pts: [number, number][], d: number, z: number, c: THREE.Color) => {
        for (let j = 1; j + 1 < pts.length; j++) tri(t, [pts[0]!, pts[j]!, pts[j + 1]!], d, z, c);
      };
      const d = droop(t);
      const z = (r() < 0.6 ? 0.035 : -0.035) + 0.001;
      // Outline, then the lit half and the shadow half either side of the midrib.
      fan(half(1, 0.07), d, z - 0.003, pal[0]!);
      fan(half(-1, 0.07), d, z - 0.003, pal[0]!);
      fan(half(1, 0), d, z, pal[2]!);
      fan(half(-1, 0), d, z, pal[1]!);
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("vine", new THREE.Float32BufferAttribute(extra, 2));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  return g;
}
